import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2, Upload } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import FloatingBulb from "@/components/FloatingBulb";
import { Reveal } from "@/components/motion";
import { Recaptcha, Honeypot, getRecaptchaToken, resetRecaptcha } from "@/components/AntiSpam";
import { SERVICES } from "@/lib/services";
import { useSeo } from "@/lib/seo";
import { fbTrack } from "@/lib/pixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AREAS = [...SERVICES.map((s) => s.name), "Altro"];
const EMPTY = { name: "", email: "", phone: "", area: "", message: "", consent: false, website: "" };
const ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export default function LavoraConNoi() {
  const [form, setForm] = useState(EMPTY);
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const captchaId = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  useSeo({
    title: "Lavora con noi | Idea 360 Consulting",
    description: "Candidati per entrare nel team di Idea 360 Consulting: finanza, marketing, HR, legale, organizzazione, formazione. Invia il tuo CV.",
    path: "/lavora-con-noi",
  });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setCv(null);
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Il CV supera i 5 MB");
      e.target.value = "";
      return setCv(null);
    }
    setCv(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!cv) return toast.error("Allega il tuo CV (PDF, DOC o DOCX)");
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("cv", cv);
      const token = getRecaptchaToken(captchaId.current);
      if (token) fd.append("recaptcha_token", token);
      await axios.post(`${API}/careers`, fd);
      fbTrack("Lead", { content_name: "Lavora con noi" });
      toast.success("Candidatura inviata. Grazie!");
      setSent(true);
      setForm(EMPTY);
      setCv(null);
      resetRecaptcha(captchaId.current);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nell'invio. Controlla i campi e riprova.");
    } finally {
      setLoading(false);
    }
  };

  const label = "block text-xs tracking-[0.2em] uppercase text-white/55 mb-2";
  return (
    <>
      <section className="relative">
        <FloatingBulb size={30} className="absolute top-24 right-[12%] hidden md:block" testId="careers-bulb" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-10 sm:pt-28 sm:pb-12">
          <Reveal as="p" y={14} className="eyebrow mb-5">Lavora con noi</Reveal>
          <Reveal as="h1" delay={0.1} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-[1.05]" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }} data-testid="careers-h1">
            Porta la tua <span className="text-[#FACC15]">competenza</span> dentro un team a 360°.
          </Reveal>
          <Reveal as="p" delay={0.2} className="mt-6 text-white/70 text-lg max-w-3xl leading-relaxed">
            Cerchiamo professionisti che vogliono lavorare accanto agli imprenditori, non solo per loro: finanza, marketing, persone, legale, organizzazione, formazione. Raccontaci chi sei e allega il CV.
          </Reveal>
        </div>
      </section>

      <section className="pb-16 sm:pb-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="border border-[#1a1a1a] bg-[#0c0c0c] p-7 sm:p-9">
            {sent ? (
              <div className="text-center py-10" data-testid="careers-success">
                <CheckCircle2 size={48} className="text-[#FACC15] mx-auto mb-5" strokeWidth={1.5} />
                <h2 className="text-2xl text-white font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Candidatura ricevuta.</h2>
                <p className="text-white/65 max-w-md mx-auto">Grazie! Leggeremo con attenzione il tuo profilo e ti ricontatteremo se c'è un'opportunità in linea.</p>
                <button type="button" className="btn-outline-yellow mt-8" onClick={() => setSent(false)} data-testid="careers-new">Invia un'altra candidatura</button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="relative" data-testid="careers-form">
                <p className="eyebrow mb-3">Candidatura</p>
                <h2 className="text-2xl sm:text-3xl text-white font-bold mb-7" style={{ fontFamily: "'Outfit', sans-serif" }}>Presentati al team.</h2>
                <Honeypot value={form.website} onChange={set("website")} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className={label} htmlFor="c-name">Nome completo *</label>
                    <input id="c-name" required minLength={2} value={form.name} onChange={set("name")} className="field-input" placeholder="Mario Rossi" data-testid="careers-name" />
                  </div>
                  <div>
                    <label className={label} htmlFor="c-email">Email *</label>
                    <input id="c-email" type="email" required value={form.email} onChange={set("email")} className="field-input" placeholder="tu@email.it" data-testid="careers-email" />
                  </div>
                  <div>
                    <label className={label} htmlFor="c-phone">Telefono *</label>
                    <input id="c-phone" required minLength={4} value={form.phone} onChange={set("phone")} className="field-input" placeholder="+39 333 0000000" data-testid="careers-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={label} htmlFor="c-area">Area di interesse *</label>
                    <select id="c-area" required value={form.area} onChange={set("area")} className="field-input" data-testid="careers-area">
                      <option value="" disabled>Scegli un'area</option>
                      {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={label} htmlFor="c-message">Presentazione *</label>
                    <textarea id="c-message" required minLength={10} rows={5} value={form.message} onChange={set("message")} className="field-input resize-none" placeholder="Chi sei, cosa sai fare, perché Idea 360..." data-testid="careers-message" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={label} htmlFor="c-cv">Curriculum (PDF, DOC, DOCX · max 5 MB) *</label>
                    <label htmlFor="c-cv" className="field-input flex items-center gap-3 cursor-pointer hover:border-[#FACC15]/60" data-testid="careers-cv-label">
                      <Upload size={18} className="text-[#FACC15] shrink-0" />
                      <span className={cv ? "text-white" : "text-white/40"}>{cv ? `${cv.name} (${Math.round(cv.size / 1024)} KB)` : "Scegli il file del tuo CV"}</span>
                    </label>
                    <input id="c-cv" type="file" accept={ACCEPT} onChange={onFile} className="sr-only" data-testid="careers-cv" />
                  </div>
                </div>

                <label className="mt-6 flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" required checked={form.consent} onChange={set("consent")} className="mt-0.5 w-5 h-5 shrink-0 accent-[#FACC15]" data-testid="careers-consent" />
                  <span className="text-xs text-white/55 leading-relaxed">
                    Dichiaro di aver letto l'<Link to="/privacy-policy" className="text-[#FACC15] underline underline-offset-2">informativa privacy</Link> e acconsento al trattamento dei miei dati e del CV ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003, come modificato dal D.Lgs. 101/2018, per finalità di selezione del personale da parte di Idea 360 Consulting. *
                  </span>
                </label>

                <Recaptcha onReady={(id) => (captchaId.current = id)} />

                <button type="submit" disabled={loading || !form.consent} className="btn-yellow mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="careers-submit">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> Invio in corso...</> : <>Invia candidatura <ArrowRight size={18} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
